import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import Swal from 'sweetalert2';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import SlideshowRoundedIcon from '@mui/icons-material/SlideshowRounded';
import ArchiveRoundedIcon from '@mui/icons-material/ArchiveRounded';
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded';
import apiurl from '../Api';
import './css/documentBucket.css';

const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt', 'ppt', 'pptx', 'zip', 'rar', '7z'];
const maxFileSizeMb = 200;
const maxFileSizeBytes = maxFileSizeMb * 1024 * 1024;
// Files bigger than maxFileSizeBytes are uploaded via the chunked path instead of a single request.
const maxChunkedFileSizeMb = 10240;
const maxChunkedFileSizeBytes = maxChunkedFileSizeMb * 1024 * 1024;
const chunkUploadSizeBytes = 20 * 1024 * 1024;
const defaultLargeUploadConfig = { enabled: false, batch_size: 100, manifest_page_size: 700, session_expiry_hours: 72 };
const defaultLargeUploadState = {
  active: false,
  stage: 'idle',
  message: '',
  sessionToken: '',
  totalFiles: 0,
  uploadedFiles: 0,
  finalizedFiles: 0,
  failedFiles: 0,
};
const defaultStandardUploadProgress = {
  active: false,
  percent: 0,
  loaded: 0,
  total: 0,
  message: '',
};

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
};

const formatFileSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = size;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(unit === 0 ? 0 : 2)} ${units[unit]}`;
};

const cleanRelativePath = (value = '') => String(value || '').replace(/^[/\\]+/, '').replace(/\\/g, '/').trim();

const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (value, query) => {
  const text = String(value ?? '');
  const search = String(query || '').trim();
  if (!search || !text) return text || 'N/A';

  const regex = new RegExp(`(${escapeRegExp(search)})`, 'ig');
  const parts = text.split(regex);

  const normalizedSearch = search.toLowerCase();

  return parts.map((part, index) => (
    part.toLowerCase() === normalizedSearch ? (
      <mark className="document-bucket-search-highlight" key={`${part}-${index}`}>{part}</mark>
    ) : (
      <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
    )
  ));
};

const getFileExtension = (row = {}) => {
  const direct = String(row.file_extension || '').trim().toLowerCase();
  if (direct) return direct;
  const fileName = String(row.original_file_name || row.file_name || row.stored_file_name || '').trim();
  return fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
};

const getDocumentVisual = (row = {}) => {
  if (row.row_type === 'folder') {
    return {
      type: 'folder',
      Icon: FolderRoundedIcon,
      label: 'Folder',
      iconClass: 'document-bucket-icon-folder',
      rowClass: 'document-bucket-row-folder',
    };
  }

  const ext = getFileExtension(row);
  if (ext === 'pdf') {
    return { type: 'pdf', Icon: PictureAsPdfRoundedIcon, label: 'PDF', iconClass: 'document-bucket-icon-pdf', rowClass: 'document-bucket-row-pdf' };
  }
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext)) {
    return { type: 'image', Icon: ImageRoundedIcon, label: 'Image', iconClass: 'document-bucket-icon-image', rowClass: 'document-bucket-row-image' };
  }
  if (['xls', 'xlsx', 'csv'].includes(ext)) {
    return { type: 'sheet', Icon: TableChartRoundedIcon, label: 'Excel', iconClass: 'document-bucket-icon-sheet', rowClass: 'document-bucket-row-sheet' };
  }
  if (['doc', 'docx'].includes(ext)) {
    return { type: 'doc', Icon: DescriptionRoundedIcon, label: 'Word', iconClass: 'document-bucket-icon-doc', rowClass: 'document-bucket-row-doc' };
  }
  if (['ppt', 'pptx'].includes(ext)) {
    return { type: 'slides', Icon: SlideshowRoundedIcon, label: 'PPT', iconClass: 'document-bucket-icon-slides', rowClass: 'document-bucket-row-slides' };
  }
  if (['zip', 'rar', '7z'].includes(ext)) {
    return { type: 'archive', Icon: ArchiveRoundedIcon, label: 'Archive', iconClass: 'document-bucket-icon-archive', rowClass: 'document-bucket-row-archive' };
  }
  if (ext === 'txt') {
    return { type: 'text', Icon: TextSnippetRoundedIcon, label: 'Text', iconClass: 'document-bucket-icon-text', rowClass: 'document-bucket-row-text' };
  }
  return { type: 'file', Icon: InsertDriveFileRoundedIcon, label: 'File', iconClass: 'document-bucket-icon-file', rowClass: 'document-bucket-row-file' };
};

const renderDocumentIcon = (row, size = 'md') => {
  const visual = getDocumentVisual(row);
  const Icon = visual.Icon;
  return (
    <span className={`document-bucket-file-icon ${visual.iconClass} document-bucket-file-icon-${size}`} title={visual.label}>
      <Icon fontSize="inherit" />
    </span>
  );
};

const extractTopFolderFromPath = (value = '') => {
  const normalized = cleanRelativePath(value);
  if (!normalized.includes('/')) return '';
  return normalized.split('/')[0] || '';
};

const readDroppedEntry = async (entry, parentPath = '') => {
  if (!entry) return [];

  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file(
        (file) => {
          const fromFile = cleanRelativePath(file?.webkitRelativePath || '');
          const fromEntry = cleanRelativePath(entry.fullPath || '');
          const fallback = cleanRelativePath(parentPath ? `${parentPath}/${file.name}` : file.name);
          const sourcePath = fromFile || fromEntry || fallback;
          resolve([{ file, sourcePath }]);
        },
        () => resolve([])
      );
    });
  }

  if (!entry.isDirectory) return [];

  const reader = entry.createReader();
  const children = [];
  while (true) {
    const batch = await new Promise((resolve) => reader.readEntries((items) => resolve(items || []), () => resolve([])));
    if (!batch.length) break;
    children.push(...batch);
  }

  const childResults = await Promise.all(
    children.map((child) => {
      const nextPath = cleanRelativePath(parentPath ? `${parentPath}/${entry.name}` : entry.name);
      return readDroppedEntry(child, nextPath);
    })
  );

  return childResults.flat();
};

const groupSelectedUploadItems = (items = []) => {
  const grouped = [];
  const folderMap = new Map();

  items.forEach((item) => {
    const folderKey = item?.sourceFolder ? `folder:${item.sourceFolder}` : '';
    if (!folderKey) {
      grouped.push({ type: 'file', item });
      return;
    }

    if (!folderMap.has(folderKey)) {
      const entry = { type: 'folder', folderName: item.sourceFolder, items: [] };
      folderMap.set(folderKey, entry);
      grouped.push(entry);
    }

    folderMap.get(folderKey).items.push(item);
  });

  return grouped;
};

const chunkItems = (items = [], size = 100) => {
  const safeSize = Math.max(1, Number(size || 1));
  const chunks = [];
  for (let index = 0; index < items.length; index += safeSize) {
    chunks.push(items.slice(index, index + safeSize));
  }
  return chunks;
};

// Uploads one file as a sequence of small requests so it can't be blocked by
// server/proxy request-size limits and a dropped connection only loses one chunk.
const chunkUploadConcurrency = 4;

const uploadOneChunk = async (sessionToken, manifestToken, file, totalChunks, chunkIndex, abortSignal) => {
  const start = chunkIndex * chunkUploadSizeBytes;
  const end = Math.min(file.size, start + chunkUploadSizeBytes);
  const blob = file.slice(start, end);

  const payload = new FormData();
  payload.append('action', 'upload_chunk');
  payload.append('session_token', sessionToken);
  payload.append('manifest_token', manifestToken);
  payload.append('chunk_index', String(chunkIndex));
  payload.append('total_chunks', String(totalChunks));
  payload.append('chunk', blob, file.name);

  const response = await axios.post(`${apiurl}document_bucket_large_upload.php`, payload, { signal: abortSignal, validateStatus: () => true });
  if (!response?.data?.status) {
    throw new Error(response?.data?.message || `Failed to upload ${file.name} (chunk ${chunkIndex + 1}/${totalChunks})`);
  }
  return { bytesSent: end - start, data: response.data };
};

// Uploads a file's chunks with a handful of requests in flight at once - much faster than
// one-at-a-time on connections where per-request latency (not raw bandwidth) is the bottleneck.
const uploadFileInChunks = async (sessionToken, manifestToken, file, onChunkProgress, abortSignal) => {
  const totalChunks = Math.max(1, Math.ceil(file.size / chunkUploadSizeBytes));
  let lastResponseData = null;
  let nextChunkIndex = 0;
  let completedChunks = 0;
  let firstError = null;

  const runWorker = async () => {
    while (true) {
      if (firstError || abortSignal?.aborted) return;
      const chunkIndex = nextChunkIndex;
      if (chunkIndex >= totalChunks) return;
      nextChunkIndex += 1;

      try {
        const result = await uploadOneChunk(sessionToken, manifestToken, file, totalChunks, chunkIndex, abortSignal);
        completedChunks += 1;
        lastResponseData = result.data;
        if (onChunkProgress) onChunkProgress(result.bytesSent, totalChunks, completedChunks);
      } catch (error) {
        if (!firstError) firstError = error;
        return;
      }
    }
  };

  const workerCount = Math.min(chunkUploadConcurrency, totalChunks);
  await Promise.all(Array.from({ length: workerCount }, () => runWorker()));

  if (firstError) throw firstError;
  return lastResponseData;
};

const getRelativePathWithinFolder = (item = {}, rootFolder = '') => {
  const sourcePath = cleanRelativePath(item?.source_path || item?.original_file_name || '');
  const normalizedRoot = cleanRelativePath(rootFolder || '');
  if (!sourcePath) return item?.original_file_name || item?.document_title || 'File';
  if (!normalizedRoot) return sourcePath;
  if (sourcePath.toLowerCase() === normalizedRoot.toLowerCase()) return item?.original_file_name || item?.document_title || sourcePath;
  if (sourcePath.toLowerCase().startsWith(`${normalizedRoot.toLowerCase()}/`)) {
    return sourcePath.slice(normalizedRoot.length + 1) || item?.original_file_name || item?.document_title || sourcePath;
  }
  return sourcePath;
};

const buildPreviewFolderTree = (items = [], rootFolder = '') => {
  const root = { type: 'folder', name: rootFolder || 'Folder', path: '', children: [] };
  const folderMap = new Map([['', root]]);

  items.forEach((item, index) => {
    const relativePath = cleanRelativePath(getRelativePathWithinFolder(item, rootFolder));
    const parts = relativePath.split('/').filter(Boolean);
    if (!parts.length) {
      parts.push(item?.original_file_name || item?.document_title || `file-${index + 1}`);
    }

    let currentPath = '';
    let parentNode = root;

    parts.forEach((part, partIndex) => {
      const isFile = partIndex === parts.length - 1;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (isFile) {
        parentNode.children.push({
          type: 'file',
          name: part,
          path: currentPath,
          item,
        });
        return;
      }

      if (!folderMap.has(currentPath)) {
        const folderNode = {
          type: 'folder',
          name: part,
          path: currentPath,
          children: [],
        };
        folderMap.set(currentPath, folderNode);
        parentNode.children.push(folderNode);
      }

      parentNode = folderMap.get(currentPath);
    });
  });

  const sortTree = (node) => {
    node.children.sort((left, right) => {
      if (left.type !== right.type) return left.type === 'folder' ? -1 : 1;
      return String(left.name || '').localeCompare(String(right.name || ''));
    });
    node.children.forEach((child) => {
      if (child.type === 'folder') sortTree(child);
    });
  };

  sortTree(root);
  return root;
};

function DocumentBucket() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total_documents: 0, total_size: 0, total_size_label: '0 B', recent_uploads: 0, my_documents: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total_records: 0, total_pages: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [permissions, setPermissions] = useState({ can_manage_categories: false, can_manage_access: false, can_view_all: false, view_scope_text: '' });
  const [userInfo, setUserInfo] = useState({ id: '', name: '', post: '', type: '' });
  const [errorMsg, setErrorMsg] = useState('');
  const [folderFileLimit, setFolderFileLimit] = useState(100);
  const [showFolderLimitModal, setShowFolderLimitModal] = useState(false);
  const [savingFolderLimit, setSavingFolderLimit] = useState(false);
  const [folderLimitInput, setFolderLimitInput] = useState('100');

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadAbortRef = useRef(null);
  const activeUploadSessionRef = useRef('');
  const [addingToFolder, setAddingToFolder] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isFolderUpload, setIsFolderUpload] = useState(false);
  const [largeUploadConfig, setLargeUploadConfig] = useState(defaultLargeUploadConfig);
  const [, setLoadingLargeUploadConfig] = useState(false);
  const [largeUploadState, setLargeUploadState] = useState(defaultLargeUploadState);
  const [standardUploadProgress, setStandardUploadProgress] = useState(defaultStandardUploadProgress);
  const [previewDoc, setPreviewDoc] = useState({ open: false, url: '', title: '', rootFolder: '', folderUploadId: '', mode: 'file', items: [], canEdit: false });
  const [expandedPreviewFolders, setExpandedPreviewFolders] = useState(['']);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewDeletingTarget, setPreviewDeletingTarget] = useState('');
  const [pickerMode, setPickerMode] = useState('drop');
  const [deletingTarget, setDeletingTarget] = useState('');
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [loadingAccessModal, setLoadingAccessModal] = useState(false);
  const [savingAccessModal, setSavingAccessModal] = useState(false);
  const [accessSearch, setAccessSearch] = useState('');
  const [accessResource, setAccessResource] = useState(null);
  const [accessStaffRows, setAccessStaffRows] = useState([]);
  const selectedUploadGroups = useMemo(() => groupSelectedUploadItems(uploadFiles), [uploadFiles]);
  const activeSearch = debouncedSearch.trim();
  const shouldUseLargeUploader = isFolderUpload
    && largeUploadConfig.enabled
    && uploadFiles.length > Number(folderFileLimit || 0);
  const shouldUseChunkedUploader = !shouldUseLargeUploader
    && largeUploadConfig.enabled
    && uploadFiles.some((item) => Number(item?.file?.size || 0) > maxFileSizeBytes);
  const previewFolderTree = useMemo(
    () => (previewDoc.mode === 'folder' ? buildPreviewFolderTree(previewDoc.items || [], previewDoc.title || previewDoc.rootFolder || '') : null),
    [previewDoc]
  );
  const largeUploadPercent = useMemo(() => {
    if (!largeUploadState.active || !largeUploadState.totalFiles) return 0;
    if (largeUploadState.stage === 'session') return 5;
    if (largeUploadState.stage === 'manifest') return 15;
    if (largeUploadState.stage === 'upload') {
      const uploadedShare = Math.min(1, Number(largeUploadState.uploadedFiles || 0) / Math.max(1, Number(largeUploadState.totalFiles || 1)));
      return Math.min(90, Math.max(20, Math.round(20 + (uploadedShare * 65))));
    }
    if (largeUploadState.stage === 'finalize') {
      const finalizedShare = Math.min(1, Number(largeUploadState.finalizedFiles || 0) / Math.max(1, Number(largeUploadState.totalFiles || 1)));
      return Math.min(98, Math.max(92, Math.round(92 + (finalizedShare * 6))));
    }
    if (largeUploadState.stage === 'completed') return 100;
    return 0;
  }, [largeUploadState]);
  const filteredAccessStaffRows = useMemo(() => {
    const query = String(accessSearch || '').trim().toLowerCase();
    const filtered = !query ? accessStaffRows : accessStaffRows.filter((entry) => (
      String(entry.name || '').toLowerCase().includes(query)
      || String(entry.post || '').toLowerCase().includes(query)
      || String(entry.staff_id || '').toLowerCase().includes(query)
    ));
    return [...filtered].sort((left, right) => {
      const leftPinned = left.is_owner || left.original_can_edit || left.original_can_view;
      const rightPinned = right.is_owner || right.original_can_edit || right.original_can_view;
      if (leftPinned !== rightPinned) return leftPinned ? -1 : 1;

      if (left.is_owner !== right.is_owner) return left.is_owner ? -1 : 1;
      if (left.original_can_edit !== right.original_can_edit) return left.original_can_edit ? -1 : 1;
      if (left.original_can_view !== right.original_can_view) return left.original_can_view ? -1 : 1;
      return Number(left.original_sort_index || 0) - Number(right.original_sort_index || 0);
    });
  }, [accessSearch, accessStaffRows]);

  const fetchDocuments = async (pageArg = pagination.page, limitArg = pagination.limit) => {
    try {
      setLoading(true);
      setErrorMsg('');
      const params = new URLSearchParams();
      params.set('action', 'list');
      params.set('page', String(pageArg));
      params.set('limit', String(limitArg));
      if (debouncedSearch) params.set('search', debouncedSearch);

      const response = await axios.get(`${apiurl}document_bucket.php?${params.toString()}`);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to fetch documents');

      setRows(response.data.data || []);
      setPagination(response.data.pagination || { page: 1, limit: 25, total_records: 0, total_pages: 0 });
      setSummary(response.data.summary || { total_documents: 0, total_size: 0, total_size_label: '0 B', recent_uploads: 0, my_documents: 0 });
      setPermissions(response.data.permissions || { can_manage_categories: false, can_manage_access: false, can_view_all: false, view_scope_text: '' });
      setUserInfo(response.data.user || { id: '', name: '', post: '', type: '' });
      setFolderFileLimit(Number(response?.data?.folder_file_limit || 100));
    } catch (error) {
      setRows([]);
      setErrorMsg(error?.message || 'Failed to fetch document list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'Document Bucket | MERAQUI';
    fetchDocuments(1, pagination.limit);
  }, []);

  // Warn before the tab is closed/refreshed mid-upload - doing so kills the
  // request outright and leaves partially-uploaded chunks on the server.
  useEffect(() => {
    if (!uploading) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchDocuments(1, pagination.limit);
  }, [debouncedSearch]);

  const resetUploadModal = () => {
    setUploadFiles([]);
    setIsDragging(false);
    setIsFolderUpload(false);
    setPickerMode('drop');
    setLargeUploadState(defaultLargeUploadState);
    setStandardUploadProgress(defaultStandardUploadProgress);
    setUploading(false);
    uploadAbortRef.current = null;
    activeUploadSessionRef.current = '';
  };

  // Aborts the in-flight upload request(s) and tells the server to drop any
  // partially-uploaded chunks/files for the session, so nothing is left orphaned.
  const abortActiveUpload = async () => {
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
    const sessionToken = activeUploadSessionRef.current;
    if (sessionToken) {
      try {
        const cancelPayload = new FormData();
        cancelPayload.append('action', 'cancel_upload_session');
        cancelPayload.append('session_token', sessionToken);
        await axios.post(`${apiurl}document_bucket_large_upload.php`, cancelPayload, { validateStatus: () => true });
      } catch (cancelError) {
        // best effort - nothing more we can do client-side
      }
    }
  };

  const requestCloseUploadModal = async () => {
    if (!uploading) {
      setShowUploadModal(false);
      resetUploadModal();
      return;
    }

    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: 'Cancel upload?',
      text: 'An upload is in progress. Closing now will stop it and delete whatever was already uploaded for this file.',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel upload',
      cancelButtonText: 'Keep uploading',
    });

    if (!confirmResult.isConfirmed) return;

    await abortActiveUpload();
    setShowUploadModal(false);
    resetUploadModal();
    fetchDocuments(1, pagination.limit);
  };

  const fetchLargeUploadConfig = async () => {
    try {
      setLoadingLargeUploadConfig(true);
      const response = await axios.get(`${apiurl}document_bucket_large_upload.php?action=config`);
      if (response?.data?.status) {
        setLargeUploadConfig({ ...defaultLargeUploadConfig, ...(response?.data?.config || {}) });
      } else {
        setLargeUploadConfig(defaultLargeUploadConfig);
      }
    } catch (error) {
      setLargeUploadConfig(defaultLargeUploadConfig);
    } finally {
      setLoadingLargeUploadConfig(false);
    }
  };

  const handleOpenUploadModal = () => {
    resetUploadModal();
    setShowUploadModal(true);
    fetchLargeUploadConfig();
  };

  const appendUploadFiles = (rawItems = [], source = 'drop') => {
    const files = (Array.isArray(rawItems) ? rawItems : Array.from(rawItems || []))
      .map((entry) => {
        if (entry?.file instanceof File) {
          return {
            file: entry.file,
            sourcePath: cleanRelativePath(entry.sourcePath || entry.file.webkitRelativePath || entry.file.name),
          };
        }
        if (entry instanceof File) {
          return {
            file: entry,
            sourcePath: cleanRelativePath(entry.webkitRelativePath || entry.name),
          };
        }
        return null;
      })
      .filter(Boolean);

    if (!files.length) return;

    const valid = [];
    const invalid = [];
    files.forEach(({ file, sourcePath }) => {
      const ext = String(file.name || '').split('.').pop()?.toLowerCase() || '';
      if (!allowedExtensions.includes(ext)) invalid.push(`${file.name} (unsupported type)`);
      else if (file.size > maxChunkedFileSizeBytes) invalid.push(`${file.name} (exceeds ${maxChunkedFileSizeMb}MB)`);
      else {
        const normalizedPath = sourcePath || cleanRelativePath(file.webkitRelativePath || file.name);
        valid.push({
          file,
          sourcePath: normalizedPath,
          sourceFolder: extractTopFolderFromPath(normalizedPath),
        });
      }
    });

    if (invalid.length) {
      Swal.fire({ icon: 'warning', title: 'File validation failed', text: invalid.slice(0, 3).join(' | ') });
    }
    if (!valid.length) return;

    const hasFolderItems = source === 'folder' || valid.some((item) => item.sourceFolder);
    if (uploadFiles.length > 0) {
      if (pickerMode === 'files' && hasFolderItems) {
        Swal.fire('Warning', 'Files mode me sirf files add kar sakte ho. Folder ke liye fresh selection use karo.', 'warning');
        return;
      }
      if (pickerMode === 'folder' && !hasFolderItems) {
        Swal.fire('Warning', 'Folder mode me sirf folder add kar sakte ho. Files ke liye fresh selection use karo.', 'warning');
        return;
      }
    }

    if (hasFolderItems) {
      const limit = Number(folderFileLimit || 0);
      if (limit > 0 && valid.length > limit && !largeUploadConfig.enabled) {
        Swal.fire('Warning', `Selected folder has ${valid.length} files. Current folder limit is ${limit}.`, 'warning');
        return;
      }
      if (limit > 0 && valid.length > limit && largeUploadConfig.enabled) {
        Swal.fire({
          icon: 'info',
          title: 'Large folder detected',
          text: `Selected folder has ${valid.length} files. Normal limit is ${limit}, so resumable large upload flow will be used.`,
        });
      }
      setIsFolderUpload(true);
      setPickerMode('folder');
    } else {
      setIsFolderUpload(false);
      if (source === 'files') {
        setPickerMode('files');
      }
    }

    const newItems = valid.map((item) => ({
      id: `${item.file.name}-${item.file.size}-${item.file.lastModified}-${Math.random().toString(16).slice(2)}`,
      file: item.file,
      sourcePath: item.sourcePath || item.file.name,
      sourceFolder: item.sourceFolder || '',
      documentTitle: '',
    }));

    setUploadFiles((prev) => [...prev, ...newItems]);
  };

  const handleUploadFilesChange = (event) => {
    appendUploadFiles(Array.from(event.target.files || []), 'files');
    if (event.target) event.target.value = '';
  };

  const handleUploadFolderChange = (event) => {
    appendUploadFiles(Array.from(event.target.files || []), 'folder');
    if (event.target) event.target.value = '';
  };

  const handleDocumentTitleChange = (id, value) => {
    setUploadFiles((prev) => prev.map((item) => (item.id === id ? { ...item, documentTitle: value } : item)));
  };

  const openFilePreview = (row) => {
    setPreviewZoom(1);
    setPreviewDoc({
      open: true,
      url: row?.file_url || '',
      title: row?.document_title || row?.original_file_name || 'Document Preview',
      rootFolder: '',
      folderUploadId: '',
      mode: 'file',
      items: [],
      canEdit: false,
    });
  };

  const openFolderPreview = (row) => {
    const items = Array.isArray(row?.folder_items) ? row.folder_items : [];
    setPreviewZoom(1);
    setExpandedPreviewFolders(['']);
    setPreviewDoc({
      open: true,
      url: items[0]?.file_url || '',
      title: row?.document_title || row?.source_folder || 'Folder Preview',
      rootFolder: row?.source_folder || row?.document_title || 'Folder',
      folderUploadId: row?.folder_upload_id || '',
      mode: 'folder',
      items,
      canEdit: Boolean(row?.can_edit),
    });
  };

  const downloadDocument = (row) => {
    if (!row) return;
    if (row.row_type === 'folder') {
      const folderId = row.folder_upload_id || '';
      if (!folderId) {
        Swal.fire('Error', 'Folder download id not found', 'error');
        return;
      }
      window.location.href = `${apiurl}document_bucket.php?action=download_folder&folder_upload_id=${encodeURIComponent(folderId)}`;
      return;
    }

    if (!row.id) {
      Swal.fire('Error', 'File download id not found', 'error');
      return;
    }
    window.location.href = `${apiurl}document_bucket.php?action=download&id=${encodeURIComponent(row.id)}`;
  };

  const addFilesToFolder = (row) => {
    const folderId = row?.folder_upload_id || '';
    if (!folderId) {
      Swal.fire('Error', 'Folder id not found', 'error');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = allowedExtensions.map((ext) => `.${ext}`).join(',');
    input.onchange = async (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      const folderName = row.source_folder || row.document_title || row.original_file_name || 'Folder';
      const formData = new FormData();
      formData.append('action', 'add_to_folder');
      formData.append('folder_upload_id', folderId);
      formData.append('folder_name', folderName);
      files.forEach((file) => {
        formData.append('documents[]', file);
        formData.append('source_folders[]', folderName);
        formData.append('source_paths[]', `${folderName}/${file.name}`);
        formData.append('document_titles[]', '');
      });

      try {
        setAddingToFolder(folderId);
        const response = await axios.post(`${apiurl}document_bucket.php`, formData);
        if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to add file');
        await Swal.fire('Success', response?.data?.message || 'File added to folder', 'success');
        fetchDocuments(pagination.page, pagination.limit);
      } catch (error) {
        Swal.fire('Error', error?.message || 'Failed to add file to folder', 'error');
      } finally {
        setAddingToFolder('');
      }
    };
    input.click();
  };

  const addFilesToNestedFolder = (node) => {
    const folderId = previewDoc?.folderUploadId || '';
    const rootFolder = previewDoc?.rootFolder || previewDoc?.title || '';
    const nestedPath = cleanRelativePath(node?.path || '');

    if (!folderId || !rootFolder) {
      Swal.fire('Error', 'Folder details not found', 'error');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = allowedExtensions.map((ext) => `.${ext}`).join(',');
    input.onchange = async (event) => {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;

      const formData = new FormData();
      formData.append('action', 'add_to_folder');
      formData.append('folder_upload_id', folderId);
      formData.append('folder_name', rootFolder);

      files.forEach((file) => {
        const relativePath = cleanRelativePath(nestedPath ? `${nestedPath}/${file.name}` : file.name);
        const fullSourcePath = cleanRelativePath(`${rootFolder}/${relativePath}`);
        formData.append('documents[]', file);
        formData.append('source_folders[]', rootFolder);
        formData.append('source_paths[]', fullSourcePath);
        formData.append('document_titles[]', '');
      });

      try {
        setAddingToFolder(`nested:${nestedPath || 'root'}`);
        const response = await axios.post(`${apiurl}document_bucket.php`, formData);
        if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to add file');

        const uploadedItems = Array.isArray(response?.data?.uploaded) ? response.data.uploaded : [];
        if (uploadedItems.length) {
          setPreviewDoc((prev) => ({
            ...prev,
            items: [
              ...(prev.items || []),
              ...uploadedItems.map((item) => ({
                id: item.id,
                file_url: item.file_url || '',
                file_size: item.file_size || 0,
                file_size_label: item.file_size_label || formatFileSize(item.file_size || 0),
                mime_type: item.mime_type || '',
                document_title: item.document_title || '',
                original_file_name: item.file_name || '',
                source_folder: item.source_folder || rootFolder,
                source_path: item.source_path || '',
                folder_upload_id: item.folder_upload_id || folderId,
                uploaded_by_name: userInfo?.name || '',
                uploaded_by_post: userInfo?.post || userInfo?.type || '',
                uploaded_by_type: userInfo?.type || '',
              })),
            ],
          }));
          setExpandedPreviewFolders((prev) => (
            nestedPath && !prev.includes(nestedPath) ? [...prev, nestedPath] : prev
          ));
        }

        await Swal.fire('Success', response?.data?.message || 'File added to folder', 'success');
        fetchDocuments(pagination.page, pagination.limit);
      } catch (error) {
        Swal.fire('Error', error?.message || 'Failed to add file to nested folder', 'error');
      } finally {
        setAddingToFolder('');
      }
    };
    input.click();
  };

  const closePreview = () => {
    setPreviewZoom(1);
    setExpandedPreviewFolders(['']);
    setPreviewDeletingTarget('');
    setPreviewDoc({ open: false, url: '', title: '', rootFolder: '', folderUploadId: '', mode: 'file', items: [], canEdit: false });
  };

  const closeAccessModal = () => {
    setShowAccessModal(false);
    setAccessSearch('');
    setAccessResource(null);
    setAccessStaffRows([]);
  };

  const handleAccessToggle = (staffId, field, checked) => {
    setAccessStaffRows((prev) => prev.map((entry) => {
      if (entry.staff_id !== staffId || entry.is_owner) return entry;
      if (field === 'can_edit') {
        return { ...entry, can_edit: checked, can_view: checked ? true : entry.can_view };
      }
      if (field === 'can_view') {
        return { ...entry, can_view: checked, can_edit: checked ? entry.can_edit : false };
      }
      return entry;
    }));
  };

  const openAccessModal = async (row) => {
    if (!row?.can_manage_access || !row) return;

    const resourceType = row.row_type === 'folder' ? 'folder' : 'document';
    const resourceKey = row.row_type === 'folder' ? String(row.folder_upload_id || '') : String(row.id || '');
    if (!resourceKey) {
      Swal.fire('Error', 'Resource key not found', 'error');
      return;
    }

    try {
      setLoadingAccessModal(true);
      setAccessSearch('');
      setShowAccessModal(true);
      const params = new URLSearchParams();
      params.set('action', 'access_details');
      params.set('resource_type', resourceType);
      params.set('resource_key', resourceKey);

      const response = await axios.get(`${apiurl}document_bucket.php?${params.toString()}`);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to load access details');

      setAccessResource(response?.data?.resource || null);
      setAccessStaffRows(
        Array.isArray(response?.data?.staff)
          ? response.data.staff.map((entry, index) => ({
            ...entry,
            original_can_view: Boolean(entry?.can_view),
            original_can_edit: Boolean(entry?.can_edit),
            original_sort_index: index,
          }))
          : []
      );
    } catch (error) {
      setShowAccessModal(false);
      Swal.fire('Error', error?.message || 'Failed to load access details', 'error');
    } finally {
      setLoadingAccessModal(false);
    }
  };

  const saveAccessModal = async () => {
    if (!accessResource) return;

    const grants = accessStaffRows
      .filter((entry) => !entry.is_owner && (entry.can_view || entry.can_edit))
      .map((entry) => ({
        staff_id: entry.staff_id,
        can_view: Boolean(entry.can_view),
        can_edit: Boolean(entry.can_edit),
      }));

    try {
      setSavingAccessModal(true);
      const formData = new FormData();
      formData.append('action', 'save_access');
      formData.append('resource_type', String(accessResource.resource_type || 'document'));
      formData.append('resource_key', String(accessResource.resource_key || ''));
      formData.append('grants', JSON.stringify(grants));

      const response = await axios.post(`${apiurl}document_bucket.php`, formData);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to save access');

      await Swal.fire('Success', response?.data?.message || 'Access updated successfully', 'success');
      closeAccessModal();
      fetchDocuments(pagination.page, pagination.limit);
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to save access', 'error');
    } finally {
      setSavingAccessModal(false);
    }
  };

  const syncPreviewAfterItemDelete = (removedItemId, removedFileUrl = '') => {
    setPreviewDoc((prev) => {
      if (prev.mode !== 'folder') return prev;
      const remainingItems = (prev.items || []).filter((item) => Number(item?.id || 0) !== Number(removedItemId || 0));
      if (!remainingItems.length) {
        return { open: false, url: '', title: '', rootFolder: '', folderUploadId: '', mode: 'file', items: [], canEdit: false };
      }

      const currentUrlDeleted = removedFileUrl && prev.url === removedFileUrl;
      return {
        ...prev,
        items: remainingItems,
        url: currentUrlDeleted ? (remainingItems[0]?.file_url || '') : prev.url,
        title: currentUrlDeleted ? (remainingItems[0]?.document_title || remainingItems[0]?.original_file_name || prev.title) : prev.title,
      };
    });
  };

  const syncPreviewAfterFolderDelete = (folderPath = '') => {
    const normalizedFolderPath = cleanRelativePath(folderPath);
    setPreviewDoc((prev) => {
      if (prev.mode !== 'folder') return prev;

      const remainingItems = (prev.items || []).filter((item) => {
        const relativePath = cleanRelativePath(getRelativePathWithinFolder(item, prev.rootFolder || prev.title || ''));
        const relativeDir = cleanRelativePath(relativePath.split('/').slice(0, -1).join('/'));
        return !(relativeDir === normalizedFolderPath || relativeDir.startsWith(`${normalizedFolderPath}/`));
      });

      if (!remainingItems.length) {
        return { open: false, url: '', title: '', rootFolder: '', folderUploadId: '', mode: 'file', items: [], canEdit: false };
      }

      const currentItem = (prev.items || []).find((item) => item?.file_url === prev.url);
      const currentRelativePath = cleanRelativePath(getRelativePathWithinFolder(currentItem, prev.rootFolder || prev.title || ''));
      const currentRelativeDir = cleanRelativePath(currentRelativePath.split('/').slice(0, -1).join('/'));
      const deletedCurrentPreview = currentRelativeDir === normalizedFolderPath || currentRelativeDir.startsWith(`${normalizedFolderPath}/`);

      return {
        ...prev,
        items: remainingItems,
        url: deletedCurrentPreview ? (remainingItems[0]?.file_url || '') : prev.url,
        title: deletedCurrentPreview ? (remainingItems[0]?.document_title || remainingItems[0]?.original_file_name || prev.title) : prev.title,
      };
    });
    setExpandedPreviewFolders((prev) => prev.filter((entry) => !(entry === normalizedFolderPath || entry.startsWith(`${normalizedFolderPath}/`))));
  };

  const deleteDocumentRow = async (row) => {
    if (!permissions?.can_manage_categories || !row) return false;

    const isFolder = row.row_type === 'folder';
    const targetName = isFolder
      ? (row.source_folder || row.document_title || 'this folder')
      : (row.document_title || row.original_file_name || 'this document');

    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: isFolder
        ? `Delete folder "${targetName}" and all files inside it?`
        : `Delete document "${targetName}"?`,
      showCancelButton: true,
      confirmButtonText: isFolder ? 'Yes, delete folder' : 'Yes, delete document',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return false;

    const requestKey = isFolder ? `folder:${row.folder_upload_id || ''}` : `file:${row.id || ''}`;
    try {
      setDeletingTarget(requestKey);
      const formData = new FormData();
      formData.append('action', isFolder ? 'delete_folder' : 'delete_document');
      if (isFolder) {
        formData.append('folder_upload_id', String(row.folder_upload_id || ''));
      } else {
        formData.append('id', String(row.id || ''));
      }

      const response = await axios.post(`${apiurl}document_bucket.php`, formData);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Delete failed');

      if (previewDoc.open) {
        if (isFolder && previewDoc.mode === 'folder' && (previewDoc.rootFolder === row.source_folder || previewDoc.title === row.document_title)) {
          closePreview();
        }
        if (!isFolder && previewDoc.mode === 'file' && previewDoc.url === row.file_url) {
          closePreview();
        }
      }

      await Swal.fire('Deleted', response?.data?.message || 'Deleted successfully', 'success');
      fetchDocuments(pagination.page, pagination.limit);
      return true;
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to delete', 'error');
      return false;
    } finally {
      setDeletingTarget('');
    }
  };

  const deletePreviewFolderBranch = async (node) => {
    if (!permissions?.can_manage_categories || !node?.path || !previewDoc?.folderUploadId) return;

    const requestKey = `folder-branch:${node.path}`;
    const confirmation = await Swal.fire({
      icon: 'warning',
      title: 'Are you sure?',
      text: `Delete folder "${node.name}" and all files inside it?`,
      showCancelButton: true,
      confirmButtonText: 'Yes, delete folder',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) return;

    try {
      setPreviewDeletingTarget(requestKey);
      const formData = new FormData();
      formData.append('action', 'delete_folder_branch');
      formData.append('folder_upload_id', String(previewDoc.folderUploadId || ''));
      formData.append('folder_path', String(node.path || ''));

      const response = await axios.post(`${apiurl}document_bucket.php`, formData);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Delete failed');

      syncPreviewAfterFolderDelete(node.path);

      await Swal.fire('Deleted', response?.data?.message || 'Folder deleted successfully', 'success');
      fetchDocuments(pagination.page, pagination.limit);
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to delete folder', 'error');
    } finally {
      setPreviewDeletingTarget('');
    }
  };

  const updatePreviewZoom = (nextZoom) => {
    setPreviewZoom((currentZoom) => {
      const numericZoom = typeof nextZoom === 'function' ? nextZoom(currentZoom) : nextZoom;
      const clampedZoom = Math.min(2, Math.max(0.2, Number(numericZoom) || 1));
      return Number(clampedZoom.toFixed(2));
    });
  };

  const togglePreviewFolder = (folderPath) => {
    const normalized = String(folderPath || '');
    setExpandedPreviewFolders((prev) => (
      prev.includes(normalized)
        ? prev.filter((entry) => entry !== normalized)
        : [...prev, normalized]
    ));
  };

  const renderPreviewTreeNode = (node, depth = 0) => {
    if (!node) return null;

    if (node.type === 'file') {
      const item = node.item || {};
      const isActive = previewDoc.url === item.file_url;
      const isDeletingFile = previewDeletingTarget === `file:${item.id || ''}` || deletingTarget === `file:${item.id || ''}`;
      return (
        <div key={`file-${node.path}`} className="document-bucket-tree-node document-bucket-tree-node-file" style={{ '--tree-depth': depth }}>
          <div className={`document-bucket-folder-file-item ${isActive ? 'active' : ''}`}>
            <button
              type="button"
              className="document-bucket-folder-file-view text-start"
              onClick={() => {
                setPreviewZoom(1);
                setPreviewDoc((prev) => ({
                  ...prev,
                  url: item.file_url || '',
                  title: item.document_title || item.original_file_name || node.name || prev.title,
                }));
              }}
            >
              <div className="d-flex align-items-center gap-2 min-w-0">
                {renderDocumentIcon(item, 'sm')}
                <div className="min-w-0">
                  <div className="fw-semibold text-truncate">{node.name}</div>
                  {permissions?.can_manage_categories && (
                    <small className="text-truncate d-block">
                      Added by: {item.uploaded_by_name || 'Unknown'} ({item.uploaded_by_post || item.uploaded_by_type || 'N/A'})
                    </small>
                  )}
                </div>
              </div>
              <small className="text-truncate d-block">{cleanRelativePath(getRelativePathWithinFolder(item, previewDoc.rootFolder || previewDoc.title || ''))}</small>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => downloadDocument(item)}
              disabled={!item.id}
              title="Download file"
            >
              <i className="ri-download-2-line" />
            </button>
            {permissions?.can_manage_categories && (
              <button
                type="button"
                className="document-bucket-tree-delete-btn"
                onClick={async () => {
                  setPreviewDeletingTarget(`file:${item.id || ''}`);
                  try {
                    const deleted = await deleteDocumentRow(item);
                    if (deleted) {
                      syncPreviewAfterItemDelete(item.id, item.file_url || '');
                    }
                  } finally {
                    setPreviewDeletingTarget('');
                  }
                }}
                disabled={!item.id || isDeletingFile}
                title="Delete file"
                aria-label="Delete file"
              >
                <i className="ri-delete-bin-line" />
              </button>
            )}
          </div>
        </div>
      );
    }

    const isExpanded = expandedPreviewFolders.includes(node.path);
    const isDeletingFolder = previewDeletingTarget === `folder-branch:${node.path}`;
    const isAddingNestedFolder = addingToFolder === `nested:${node.path || 'root'}`;
    return (
      <div key={`folder-${node.path || 'root'}`} className="document-bucket-tree-node document-bucket-tree-node-folder" style={{ '--tree-depth': depth }}>
        {node.path ? (
          <div className="document-bucket-tree-folder-row">
            <button
              type="button"
              className="document-bucket-tree-folder-toggle"
              onClick={() => togglePreviewFolder(node.path)}
            >
              <span className="document-bucket-tree-caret"><i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line`} /></span>
              {renderDocumentIcon({ row_type: 'folder' }, 'sm')}
              <span className="text-truncate fw-semibold">{node.name}</span>
              <span className="document-bucket-tree-folder-meta">{node.children.length}</span>
            </button>
            {previewDoc?.canEdit && (
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                onClick={() => addFilesToNestedFolder(node)}
                disabled={!previewDoc?.folderUploadId || isAddingNestedFolder}
                title="Add files to this folder"
                aria-label="Add files to this folder"
              >
                <i className="ri-add-line" />
              </button>
            )}
            {permissions?.can_manage_categories && (
              <button
                type="button"
                className="document-bucket-tree-delete-btn"
                onClick={() => deletePreviewFolderBranch(node)}
                disabled={isDeletingFolder}
                title="Delete folder"
                aria-label="Delete folder"
              >
                <i className="ri-delete-bin-line" />
              </button>
            )}
          </div>
        ) : null}
        {(node.path === '' || isExpanded) && (
          <div className="document-bucket-tree-children">
            {node.children.map((child) => renderPreviewTreeNode(child, node.path ? depth + 1 : depth))}
          </div>
        )}
      </div>
    );
  };

  const renderPreviewFrame = () => (
    previewDoc.url ? (
      <div className="document-bucket-preview-stage">
        <div
          className="document-bucket-preview-scale"
          style={{
            width: `${100 / previewZoom}%`,
            height: `${100 / previewZoom}%`,
            transform: `scale(${previewZoom})`,
          }}
        >
          <iframe src={previewDoc.url} title="document-preview-large" className="document-bucket-preview-iframe-large" />
        </div>
      </div>
    ) : (
      <div className="p-4 text-muted">No preview available</div>
    )
  );

  const handleFileDrop = async (event) => {
    event.preventDefault();
    setIsDragging(false);

    const transferItems = Array.from(event.dataTransfer?.items || []);
    const hasEntryApi = transferItems.some((item) => typeof item?.webkitGetAsEntry === 'function');

    if (hasEntryApi) {
      const entryResults = await Promise.all(
        transferItems.map(async (item) => {
          const entry = item.webkitGetAsEntry?.();
          if (!entry) {
            const looseFile = item.getAsFile?.();
            if (!looseFile) return [];
            return [{ file: looseFile, sourcePath: cleanRelativePath(looseFile.webkitRelativePath || looseFile.name) }];
          }
          return readDroppedEntry(entry);
        })
      );
      appendUploadFiles(entryResults.flat(), 'drop');
      return;
    }

    appendUploadFiles(Array.from(event.dataTransfer?.files || []), 'drop');
  };

  const handleUploadSubmit = async (event) => {
    event.preventDefault();

    if (!uploadFiles.length) {
      Swal.fire('Warning', 'Please choose at least one file', 'warning');
      return;
    }

    const abortController = new AbortController();
    uploadAbortRef.current = abortController;
    activeUploadSessionRef.current = '';

    try {
      setUploading(true);
      if (shouldUseLargeUploader) {
        const folderName = uploadFiles[0]?.sourceFolder || 'Large Folder';
        setLargeUploadState({
          active: true,
          stage: 'session',
          message: 'Creating upload session...',
          sessionToken: '',
          totalFiles: uploadFiles.length,
          uploadedFiles: 0,
          finalizedFiles: 0,
          failedFiles: 0,
        });

        const createPayload = new FormData();
        createPayload.append('action', 'create_upload_session');
        createPayload.append('folder_name', folderName);
        createPayload.append('upload_mode', 'folder');
        createPayload.append('total_files', String(uploadFiles.length));
        createPayload.append('total_bytes', String(uploadFiles.reduce((sum, item) => sum + Number(item?.file?.size || 0), 0)));
        const createResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, createPayload, { signal: abortController.signal, validateStatus: () => true });
        if (!createResponse?.data?.status) throw new Error(createResponse?.data?.message || 'Failed to create large upload session');

        const sessionToken = String(createResponse?.data?.session?.session_token || '');
        if (!sessionToken) throw new Error('Large upload session token missing');
        activeUploadSessionRef.current = sessionToken;

        const manifestPageSize = Number(createResponse?.data?.config?.manifest_page_size || largeUploadConfig.manifest_page_size || 700);
        const batchSize = Number(createResponse?.data?.config?.batch_size || largeUploadConfig.batch_size || 100);
        const manifestChunks = chunkItems(uploadFiles, manifestPageSize);
        let manifestIndex = 0;
        const manifestRegistry = new Map();

        for (const manifestChunk of manifestChunks) {
          manifestIndex += 1;
          setLargeUploadState({
            active: true,
            stage: 'manifest',
            sessionToken,
            message: `Registering manifest ${manifestIndex}/${manifestChunks.length}...`,
            totalFiles: uploadFiles.length,
            uploadedFiles: 0,
            finalizedFiles: 0,
            failedFiles: 0,
          });

          const manifestPayload = new FormData();
          manifestPayload.append('action', 'register_upload_manifest');
          manifestPayload.append('session_token', sessionToken);
          manifestChunk.forEach((item) => {
            manifestPayload.append('relative_paths[]', String(item.sourcePath || item.file?.name || '').trim());
            manifestPayload.append('file_names[]', item.file?.name || '');
            manifestPayload.append('document_titles[]', String(item.documentTitle || '').trim());
            manifestPayload.append('sizes[]', String(item.file?.size || 0));
            manifestPayload.append('last_modified[]', String(item.file?.lastModified || 0));
          });

          const manifestResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, manifestPayload, { signal: abortController.signal, validateStatus: () => true });
          if (!manifestResponse?.data?.status) throw new Error(manifestResponse?.data?.message || 'Manifest registration failed');

          (manifestResponse?.data?.inserted || []).forEach((entry) => {
            manifestRegistry.set(entry.relative_path, entry.manifest_token);
          });
          (manifestResponse?.data?.skipped || []).forEach((entry) => {
            if (entry?.relative_path && entry?.manifest_token) {
              manifestRegistry.set(entry.relative_path, entry.manifest_token);
            }
          });
        }

        const uploadChunks = chunkItems(uploadFiles, batchSize);
        let uploadedCount = 0;
        let failedCount = 0;
        for (let batchIndex = 0; batchIndex < uploadChunks.length; batchIndex += 1) {
          const batch = uploadChunks[batchIndex];
          setLargeUploadState({
            active: true,
            stage: 'upload',
            sessionToken,
            uploadedFiles: uploadedCount,
            failedFiles: failedCount,
            message: `Uploading batch ${batchIndex + 1}/${uploadChunks.length}...`,
            totalFiles: uploadFiles.length,
            finalizedFiles: 0,
          });

          const batchPayload = new FormData();
          batchPayload.append('action', 'upload_batch');
          batchPayload.append('session_token', sessionToken);
          batchPayload.append('batch_no', String(batchIndex + 1));

          batch.forEach((item) => {
            const relativePath = String(item.sourcePath || item.file?.name || '').trim();
            const manifestToken = manifestRegistry.get(relativePath);
            if (!manifestToken) {
              throw new Error(`Manifest token missing for ${relativePath}`);
            }
            batchPayload.append('manifest_tokens[]', manifestToken);
            batchPayload.append('documents[]', item.file);
          });

          const batchResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, batchPayload, { signal: abortController.signal, validateStatus: () => true });
          if (!batchResponse?.data?.status) throw new Error(batchResponse?.data?.message || 'Batch upload failed');
          uploadedCount += Number(batchResponse?.data?.success_count || 0);
          failedCount += Number(batchResponse?.data?.failed_count || 0);
        }

        setLargeUploadState((prev) => ({
          ...prev,
          active: true,
          stage: 'finalize',
          sessionToken,
          uploadedFiles: uploadedCount,
          failedFiles: failedCount,
          message: 'Finalizing uploaded files...',
        }));

        const finalizePayload = new FormData();
        finalizePayload.append('action', 'finalize_upload_session');
        finalizePayload.append('session_token', sessionToken);
        const finalizeResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, finalizePayload, { signal: abortController.signal, validateStatus: () => true });
        if (!finalizeResponse?.data?.status) throw new Error(finalizeResponse?.data?.message || 'Finalize failed');
        const finalizedCount = Number(finalizeResponse?.data?.finalized_count || uploadedCount);

        const finalizeFailedText = (finalizeResponse?.data?.failed || [])
          .slice(0, 2)
          .map((item) => `${item.file_name}: ${item.reason}`)
          .join(' | ');

        setLargeUploadState((prev) => ({
          ...prev,
          active: true,
          stage: 'completed',
          message: 'Large upload completed successfully.',
          uploadedFiles: uploadedCount,
          finalizedFiles: finalizedCount,
          failedFiles: Number(finalizeResponse?.data?.failed_count || failedCount),
        }));

        await Swal.fire({
          icon: 'success',
          title: 'Large upload complete',
          text: finalizeFailedText ? `${finalizeResponse?.data?.message || 'Upload completed'}. ${finalizeFailedText}` : (finalizeResponse?.data?.message || 'Upload completed'),
        });
      } else if (shouldUseChunkedUploader) {
        setLargeUploadState({
          active: true,
          stage: 'session',
          message: 'Creating upload session...',
          sessionToken: '',
          totalFiles: uploadFiles.length,
          uploadedFiles: 0,
          finalizedFiles: 0,
          failedFiles: 0,
        });

        const totalBytes = uploadFiles.reduce((sum, item) => sum + Number(item?.file?.size || 0), 0);
        const createPayload = new FormData();
        createPayload.append('action', 'create_upload_session');
        createPayload.append('folder_name', uploadFiles[0]?.file?.name || 'Files');
        createPayload.append('upload_mode', 'files');
        createPayload.append('total_files', String(uploadFiles.length));
        createPayload.append('total_bytes', String(totalBytes));
        const createResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, createPayload, { signal: abortController.signal, validateStatus: () => true });
        if (!createResponse?.data?.status) throw new Error(createResponse?.data?.message || 'Failed to create upload session');

        const sessionToken = String(createResponse?.data?.session?.session_token || '');
        if (!sessionToken) throw new Error('Upload session token missing');
        activeUploadSessionRef.current = sessionToken;

        setLargeUploadState((prev) => ({ ...prev, stage: 'manifest', sessionToken, message: 'Registering files...' }));

        const manifestPayload = new FormData();
        manifestPayload.append('action', 'register_upload_manifest');
        manifestPayload.append('session_token', sessionToken);
        uploadFiles.forEach((item) => {
          manifestPayload.append('relative_paths[]', String(item.sourcePath || item.file?.name || '').trim());
          manifestPayload.append('file_names[]', item.file?.name || '');
          manifestPayload.append('document_titles[]', String(item.documentTitle || '').trim());
          manifestPayload.append('sizes[]', String(item.file?.size || 0));
          manifestPayload.append('last_modified[]', String(item.file?.lastModified || 0));
        });

        const manifestResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, manifestPayload, { signal: abortController.signal, validateStatus: () => true });
        if (!manifestResponse?.data?.status) throw new Error(manifestResponse?.data?.message || 'Manifest registration failed');

        const manifestRegistry = new Map();
        (manifestResponse?.data?.inserted || []).forEach((entry) => {
          manifestRegistry.set(entry.relative_path, entry.manifest_token);
        });
        (manifestResponse?.data?.skipped || []).forEach((entry) => {
          if (entry?.relative_path && entry?.manifest_token) manifestRegistry.set(entry.relative_path, entry.manifest_token);
        });

        let uploadedBytes = 0;
        let failedCount = 0;
        const chunkFailureReasons = [];
        for (let fileIndex = 0; fileIndex < uploadFiles.length; fileIndex += 1) {
          const item = uploadFiles[fileIndex];
          const relativePath = String(item.sourcePath || item.file?.name || '').trim();
          const manifestToken = manifestRegistry.get(relativePath);
          if (!manifestToken) throw new Error(`Manifest token missing for ${relativePath}`);

          try {
            await uploadFileInChunks(sessionToken, manifestToken, item.file, (bytesSent, totalChunks, chunkNo) => {
              uploadedBytes += bytesSent;
              setLargeUploadState((prev) => ({
                ...prev,
                active: true,
                stage: 'upload',
                sessionToken,
                uploadedFiles: fileIndex,
                failedFiles: failedCount,
                totalFiles: uploadFiles.length,
                finalizedFiles: 0,
                message: `Uploading ${item.file.name} (chunk ${chunkNo}/${totalChunks})...`,
              }));
            }, abortController.signal);
          } catch (chunkError) {
            if (axios.isCancel(chunkError) || chunkError?.name === 'CanceledError' || abortController.signal.aborted) {
              throw chunkError;
            }
            failedCount += 1;
            chunkFailureReasons.push(`${item.file.name}: ${chunkError?.message || 'Upload failed'}`);
          }
        }

        if (failedCount >= uploadFiles.length) {
          throw new Error(chunkFailureReasons.join(' | ') || 'All files failed to upload');
        }

        setLargeUploadState((prev) => ({
          ...prev,
          active: true,
          stage: 'finalize',
          sessionToken,
          uploadedFiles: uploadFiles.length - failedCount,
          failedFiles: failedCount,
          message: 'Finalizing uploaded files...',
        }));

        const finalizePayload = new FormData();
        finalizePayload.append('action', 'finalize_upload_session');
        finalizePayload.append('session_token', sessionToken);
        const finalizeResponse = await axios.post(`${apiurl}document_bucket_large_upload.php`, finalizePayload, { signal: abortController.signal, validateStatus: () => true });
        if (!finalizeResponse?.data?.status) throw new Error(finalizeResponse?.data?.message || 'Finalize failed');
        const finalizedCount = Number(finalizeResponse?.data?.finalized_count || 0);

        const finalizeFailedText = (finalizeResponse?.data?.failed || [])
          .slice(0, 2)
          .map((entry) => `${entry.file_name}: ${entry.reason}`)
          .join(' | ');

        setLargeUploadState((prev) => ({
          ...prev,
          active: true,
          stage: 'completed',
          message: 'Upload completed successfully.',
          finalizedFiles: finalizedCount,
          failedFiles: Number(finalizeResponse?.data?.failed_count || failedCount),
        }));

        await Swal.fire({
          icon: finalizedCount > 0 ? 'success' : 'warning',
          title: 'Upload complete',
          text: finalizeFailedText ? `${finalizeResponse?.data?.message || 'Upload completed'}. ${finalizeFailedText}` : (finalizeResponse?.data?.message || 'Upload completed'),
        });
      } else {
        const formData = new FormData();
        formData.append('action', 'upload');
        formData.append('is_folder_upload', isFolderUpload ? '1' : '0');

        uploadFiles.forEach((item) => {
          formData.append('documents[]', item.file);
          formData.append('document_titles[]', String(item.documentTitle || '').trim());
          formData.append('source_folders[]', String(item.sourceFolder || '').trim());
          formData.append('source_paths[]', String(item.sourcePath || item.file?.name || '').trim());
        });

        setStandardUploadProgress({
          active: true,
          percent: 0,
          loaded: 0,
          total: uploadFiles.reduce((sum, item) => sum + Number(item?.file?.size || 0), 0),
          message: 'Uploading files...',
        });

        const response = await axios.post(`${apiurl}document_bucket.php`, formData, {
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            const total = Number(progressEvent?.total || 0);
            const loaded = Number(progressEvent?.loaded || 0);
            const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : 0;
            setStandardUploadProgress({
              active: true,
              percent,
              loaded,
              total,
              message: percent >= 100 ? 'Processing uploaded files...' : 'Uploading files...',
            });
          },
        });
        if (!response?.data?.status) throw new Error(response?.data?.message || 'Upload failed');

        const failedText = (response?.data?.failed || [])
          .slice(0, 2)
          .map((item) => `${item.file_name}: ${item.reason}`)
          .join(' | ');

        await Swal.fire({ icon: 'success', title: 'Upload complete', text: failedText ? `${response.data.message}. ${failedText}` : response.data.message });
      }
      setShowUploadModal(false);
      resetUploadModal();
      fetchDocuments(1, pagination.limit);
    } catch (error) {
      const wasCancelled = axios.isCancel(error) || error?.name === 'CanceledError' || abortController.signal.aborted;
      if (!wasCancelled) {
        Swal.fire('Error', error?.message || 'Upload failed', 'error');
      }
    } finally {
      setUploading(false);
      setStandardUploadProgress((prev) => (prev.active ? { ...prev, message: 'Upload finished' } : prev));
    }
  };

  const saveFolderFileLimit = async () => {
    const nextLimit = Number(folderLimitInput || 0);
    if (!Number.isFinite(nextLimit) || nextLimit < 1 || nextLimit > 5000) {
      Swal.fire('Warning', 'Folder limit must be between 1 and 5000', 'warning');
      return;
    }
    try {
      setSavingFolderLimit(true);
      const formData = new FormData();
      formData.append('action', 'set_folder_limit');
      formData.append('folder_file_limit', String(nextLimit));
      const response = await axios.post(`${apiurl}document_bucket.php`, formData);
      if (!response?.data?.status) throw new Error(response?.data?.message || 'Failed to save folder limit');
      setFolderFileLimit(Number(response?.data?.folder_file_limit || nextLimit));
      setShowFolderLimitModal(false);
      Swal.fire('Success', response?.data?.message || 'Folder limit updated', 'success');
    } catch (error) {
      Swal.fire('Error', error?.message || 'Failed to save folder limit', 'error');
    } finally {
      setSavingFolderLimit(false);
    }
  };

  const handleRowsPerPageChange = (nextLimit) => {
    const safeLimit = Number(nextLimit || 25);
    setPagination((prev) => ({ ...prev, page: 1, limit: safeLimit }));
    fetchDocuments(1, safeLimit);
  };

  const columns = useMemo(
    () => [
      {
        name: 'S.No',
        selector: (row) => row.sr_no,
        width: '160px',
        sortable: true,
        cell: (row) => {
          const isFolder = row.row_type === 'folder';
          const isDeleting = deletingTarget === `${isFolder ? 'folder' : 'file'}:${isFolder ? (row.folder_upload_id || '') : (row.id || '')}`;
          const canDelete = permissions?.can_manage_categories
            && (isFolder ? Boolean(row.folder_upload_id) : Boolean(row.id));
          const canManageAccess = Boolean(row?.can_manage_access)
            && (isFolder ? Boolean(row.folder_upload_id) : Boolean(row.id));

          return (
            <div className="document-bucket-sno-cell">
              {renderDocumentIcon(row)}
              <span>{row.sr_no}</span>
              {canManageAccess && (
                <button
                  type="button"
                  className="document-bucket-sno-access-btn"
                  onClick={() => openAccessModal(row)}
                  title={isFolder ? 'Manage Folder Access' : 'Manage Document Access'}
                  aria-label={isFolder ? 'Manage Folder Access' : 'Manage Document Access'}
                >
                  <i className="ri-shield-user-line" />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  className="document-bucket-sno-delete-btn"
                  onClick={() => deleteDocumentRow(row)}
                  disabled={isDeleting}
                  title={isFolder ? 'Delete Folder' : 'Delete File'}
                  aria-label={isFolder ? 'Delete Folder' : 'Delete File'}
                >
                  <i className="ri-delete-bin-line" />
                </button>
              )}
            </div>
          );
        },
      },
      {
        name: 'Document', sortable: true, minWidth: '260px',
        cell: (row) => (
          <div className="document-bucket-document-cell">
            <div className="d-flex align-items-center gap-2">
              <div className="fw-semibold">{highlightText(row.row_type === 'folder' ? row.source_folder || row.document_title || 'Folder' : row.document_title || row.original_file_name || 'N/A', activeSearch)}</div>
              <span className={`document-bucket-kind-pill ${getDocumentVisual(row).rowClass}`}>{highlightText(getDocumentVisual(row).label, activeSearch)}</span>
            </div>
            <div className="text-muted" style={{ fontSize: '12px' }}>
              {row.row_type === 'folder'
                ? `${Number(row.folder_file_count || 0)} file(s)${Number(row.subfolder_count || 0) > 0 ? ` • ${Number(row.subfolder_count || 0)} subfolder(s)` : ''}`
                : highlightText(row.original_file_name || 'N/A', activeSearch)}
            </div>
            {row.row_type === 'folder'
              ? (
                <div className="text-muted" style={{ fontSize: '11px' }}>
                  {highlightText(
                    row.has_nested_folders
                      ? `Uploaded as nested folder batch (depth ${Number(row.max_depth || 1)})`
                      : 'Uploaded as folder batch',
                    activeSearch
                  )}
                </div>
                )
              : (row.source_folder ? <div className="text-muted" style={{ fontSize: '11px' }}>From folder: {highlightText(row.source_folder, activeSearch)}</div> : null)}
          </div>
        ),
      },
      { name: 'Size', selector: (row) => row.file_size_label || '0 B', width: '110px', sortable: true, cell: (row) => highlightText(row.file_size_label || '0 B', activeSearch) },
      { name: 'Uploaded At', selector: (row) => formatDateTime(row.uploaded_at), width: '160px', sortable: true, cell: (row) => highlightText(formatDateTime(row.uploaded_at), activeSearch) },
      {
        name: 'Created By', width: '180px', sortable: true,
        cell: (row) => (
          <div>
            <div className="fw-semibold">{highlightText(row.owner_name || row.uploaded_by_name || 'N/A', activeSearch)}</div>
            <small className="text-muted">{highlightText(row.owner_post || row.owner_type || row.uploaded_by_post || row.uploaded_by_type || 'N/A', activeSearch)}</small>
          </div>
        ),
      },
      {
        name: 'Actions', minWidth: '320px',
        cell: (row) => (
          <div className="document-bucket-action-group">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => (row.row_type === 'folder' ? openFolderPreview(row) : openFilePreview(row))}
              disabled={row.row_type !== 'folder' && !row.file_url}
            >
              {row.row_type === 'folder' ? 'View Folder' : 'View File'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-success"
              onClick={() => downloadDocument(row)}
              disabled={row.row_type === 'folder' ? !row.folder_upload_id : !row.id}
            >
              <i className="ri-download-2-line me-1" /> Download
            </button>
            {row.row_type === 'folder' && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => addFilesToFolder(row)}
                disabled={!row.folder_upload_id || addingToFolder === row.folder_upload_id || !row.can_edit}
              >
                <i className="ri-file-add-line me-1" /> {addingToFolder === row.folder_upload_id ? 'Adding...' : 'Add File'}
              </button>
            )}
          </div>
        ),
      },
    ],
    [activeSearch, addingToFolder, deletingTarget, permissions, previewDoc, pagination.page, pagination.limit]
  );

  const tableCustomStyles = useMemo(
    () => ({
      headRow: {
        style: {
          backgroundColor: 'var(--primary-color)',
          color: '#ffffff',
          minHeight: '58px',
          borderTop: '1px solid #e8ebf3',
          borderLeft: '1px solid #e8ebf3',
          borderRight: '1px solid #e8ebf3',
        },
      },
      headCells: {
        style: {
          fontSize: '13px',
          fontWeight: 600,
          minHeight: '58px',
          paddingLeft: '12px',
          paddingRight: '12px',
          borderRight: '1px solid rgba(255,255,255,0.18)',
        },
      },
      rows: {
        style: {
          minHeight: '58px',
          borderLeft: '1px solid #e8ebf3',
          borderRight: '1px solid #e8ebf3',
          borderBottom: '1px solid #e8ebf3',
        },
      },
      cells: {
        style: {
          paddingLeft: '12px',
          paddingRight: '12px',
          fontSize: '13px',
        },
      },
      pagination: {
        style: {
          borderLeft: '1px solid #e8ebf3',
          borderRight: '1px solid #e8ebf3',
          borderBottom: '1px solid #e8ebf3',
          borderTop: 'none',
          minHeight: '52px',
        },
      },
    }),
    []
  );

  const tableConditionalRowStyles = useMemo(
    () => [
      { when: (row) => getDocumentVisual(row).type === 'folder', style: { backgroundColor: '#fffbeb' } },
      { when: (row) => getDocumentVisual(row).type === 'sheet', style: { backgroundColor: '#f0fdf4' } },
      { when: (row) => getDocumentVisual(row).type === 'pdf', style: { backgroundColor: '#fff5f5' } },
      { when: (row) => getDocumentVisual(row).type === 'image', style: { backgroundColor: '#f0f9ff' } },
      { when: (row) => getDocumentVisual(row).type === 'doc', style: { backgroundColor: '#eff6ff' } },
      { when: (row) => getDocumentVisual(row).type === 'slides', style: { backgroundColor: '#fff7ed' } },
      { when: (row) => getDocumentVisual(row).type === 'archive', style: { backgroundColor: '#f8fafc' } },
      { when: (row) => getDocumentVisual(row).type === 'text', style: { backgroundColor: '#f7fee7' } },
    ],
    []
  );

  return (
    <div className="main-content app-content document-bucket-page">
      <div className="container-fluid" style={{ marginTop: '20px' }}>
        <div className="card custom-card border-0 shadow-sm mb-3 document-bucket-summary-card">
          <div className="card-body p-3 p-xl-4">
            <div className="row g-3">
              <div className="col-12 col-md-6 col-xl-3"><div className="document-bucket-stat-card document-bucket-stat-card-total"><small>Total Documents</small><h4>{Number(summary.total_documents || 0)}</h4></div></div>
              <div className="col-12 col-md-6 col-xl-3"><div className="document-bucket-stat-card document-bucket-stat-card-storage"><small>Total Storage</small><h4>{summary.total_size_label || '0 B'}</h4></div></div>
              <div className="col-12 col-md-6 col-xl-3"><div className="document-bucket-stat-card document-bucket-stat-card-recent"><small>Recent Uploads (7 days)</small><h4>{Number(summary.recent_uploads || 0)}</h4></div></div>
              <div className="col-12 col-md-6 col-xl-3"><div className="document-bucket-stat-card document-bucket-stat-card-mine"><small>My Documents</small><h4>{Number(summary.my_documents || 0)}</h4></div></div>
            </div>
          </div>
        </div>

        <div className="card custom-card border-0 shadow-sm document-bucket-table-card">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h2 className="mb-0 fw-bold document-bucket-title">Document Bucket</h2>
              <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="btn btn-primary" onClick={handleOpenUploadModal}>
                  <i className="ri-upload-cloud-2-line me-1" /> Add Document
                </button>
                {/* {permissions.can_manage_categories && (
                  <button type="button" className="btn btn-outline-primary" onClick={() => { setFolderLimitInput(String(folderFileLimit)); setShowFolderLimitModal(true); }}>
                    <i className="ri-folder-chart-line me-1" /> Add Folder Limit
                  </button>
                )} */}
              </div>
            </div>
            <div className="row g-3 align-items-end mb-3">
              <div className="col-12 col-lg-6">
                
                <div className="document-bucket-input-wrap">
                  <i className="ri-search-line" />
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by title, file, folder, type, user, path"
                    value={searchInput}
                    onChange={(event) => { setSearchInput(event.target.value); setPagination((prev) => ({ ...prev, page: 1 })); }}
                  />
                </div>
              </div>
            </div>

            {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

            <DataTable
              columns={columns}
              data={rows}
              customStyles={tableCustomStyles}
              conditionalRowStyles={tableConditionalRowStyles}
              progressPending={loading}
              pagination
              paginationServer
              paginationComponentOptions={{ rowsPerPageText: 'Rows per page', rangeSeparatorText: 'of', selectAllRowsItem: false }}
              paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
              paginationTotalRows={pagination.total_records || 0}
              paginationPerPage={pagination.limit || 25}
              paginationDefaultPage={pagination.page || 1}
              onChangePage={(nextPage) => { setPagination((prev) => ({ ...prev, page: nextPage })); fetchDocuments(nextPage, pagination.limit); }}
              onChangeRowsPerPage={handleRowsPerPageChange}
              highlightOnHover
              dense
              persistTableHead
              className="document-bucket-data-table"
              noDataComponent={<div className="py-4 text-muted">No documents found</div>}
            />

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-1">
              <small className="text-muted">Logged in as {userInfo?.name || 'User'} ({userInfo?.post || userInfo?.type || '-'})</small>
              <small className="text-muted">Page {pagination.page || 1} of {pagination.total_pages || 1}</small>
            </div>
          </div>
        </div>
      </div>

      {showUploadModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header document-bucket-modal-header">
                  <h5 className="modal-title mb-0">Upload Documents</h5>
                  <button type="button" className="btn-close" onClick={requestCloseUploadModal} />
                </div>

                <form onSubmit={handleUploadSubmit}>
                  <div className="modal-body">
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <label className="btn btn-outline-primary mb-0">
                        <i className="ri-file-upload-line me-1" /> Upload Files
                        <input type="file" className="d-none" multiple accept={allowedExtensions.map((ext) => `.${ext}`).join(',')} onChange={handleUploadFilesChange} />
                      </label>
                      <label className="btn btn-outline-primary mb-0">
                        <i className="ri-folder-upload-line me-1" /> Upload Folder
                        <input type="file" className="d-none" multiple webkitdirectory="" directory="" onChange={handleUploadFolderChange} />
                      </label>
                    </div>

                    <div
                      className={`document-bucket-dropzone ${isDragging ? 'dragging' : ''}`}
                      onDragOver={(event) => { event.preventDefault(); setIsDragging(true); }}
                      onDragLeave={(event) => { event.preventDefault(); setIsDragging(false); }}
                      onDrop={handleFileDrop}
                    >
                      <div className="document-bucket-dropzone-inner">
                        <div className="document-bucket-dropzone-icon"><i className="ri-upload-cloud-2-line" /></div>
                        <h6 className="mb-1">Drag and drop files or folders here</h6>
                        
                        <small>Allowed formats: {allowedExtensions.join(', ')} | Max {maxFileSizeMb}MB per file (larger files up to {Math.round(maxChunkedFileSizeMb / 1024)}GB upload in chunks automatically)</small>
                        <small className="d-block mt-1">
                          {pickerMode === 'folder' ? `Folder mode selected (max ${folderFileLimit} files)` : pickerMode === 'files' ? 'Files mode selected' : isFolderUpload ? `Folder detected from drop (max ${folderFileLimit} files)` : 'Waiting for file or folder'}
                        </small>
                      </div>
                    </div>

                    {uploadFiles.length > 0 && (
                      <div className="mt-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                          <h6 className="fw-semibold mb-0">Selected Files ({uploadFiles.length})</h6>
                          {shouldUseLargeUploader && <span className="badge bg-success-subtle text-success border">Large resumable upload</span>}
                        </div>

                        <div className="document-bucket-file-list">
                          {selectedUploadGroups.map((group, index) => (
                            group.type === 'folder' ? (
                              <div className="document-bucket-file-row" key={`folder-${group.folderName}-${index}`}>
                                <div className="document-bucket-file-main">
                                  <div className="fw-semibold text-truncate" title={group.folderName}>{group.folderName}</div>
                                  <small className="text-muted">{group.items.length} file(s) selected as folder upload</small>
                                </div>

                                <div className="document-bucket-type-wrap">
                                  <span className="text-muted small">Folder titles will use individual file titles only if set later.</span>
                                </div>

                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setUploadFiles((prev) => prev.filter((row) => row.sourceFolder !== group.folderName))}>
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            ) : (
                              <div className="document-bucket-file-row" key={group.item.id}>
                                <div className="document-bucket-file-main">
                                  <div className="fw-semibold text-truncate" title={group.item.file.name}>{group.item.file.name}</div>
                                  <small className="text-muted">{formatFileSize(group.item.file.size)}</small>
                                </div>

                                <div className="document-bucket-type-wrap">
                                  <input type="text" className="form-control form-control-sm" value={group.item.documentTitle} onChange={(event) => handleDocumentTitleChange(group.item.id, event.target.value)} placeholder="Document title (optional)" />
                                </div>

                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setUploadFiles((prev) => prev.filter((row) => row.id !== group.item.id))}>
                                  <i className="ri-delete-bin-line" />
                                </button>
                              </div>
                            )
                          ))}
                        </div>
                      </div>
                    )}

                    {largeUploadState.active && (
                      <div className="alert alert-primary mt-3 mb-0">
                        <div className="fw-semibold mb-1">Large Upload Progress</div>
                        <div>{largeUploadState.message || 'Preparing large upload...'}</div>
                        <div className="document-bucket-progress-track mt-2">
                          <div
                            className="document-bucket-progress-fill document-bucket-progress-fill-primary"
                            style={{ width: `${largeUploadPercent}%` }}
                            role="progressbar"
                            aria-valuenow={largeUploadPercent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                        <small className="d-block mt-1">
                          {largeUploadPercent}% complete | Uploaded: {largeUploadState.uploadedFiles} / {largeUploadState.totalFiles} | Finalized: {largeUploadState.finalizedFiles} | Failed: {largeUploadState.failedFiles}
                        </small>
                      </div>
                    )}

                    {standardUploadProgress.active && !shouldUseLargeUploader && (
                      <div className="alert alert-info mt-3 mb-0">
                        <div className="fw-semibold mb-1">Upload Progress</div>
                        <div>{standardUploadProgress.message || 'Uploading files...'}</div>
                        <div className="document-bucket-progress-track mt-2">
                          <div
                            className="document-bucket-progress-fill document-bucket-progress-fill-info"
                            style={{ width: `${standardUploadProgress.percent}%` }}
                            role="progressbar"
                            aria-valuenow={standardUploadProgress.percent}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          />
                        </div>
                        <small className="d-block mt-1">
                          {standardUploadProgress.percent}% complete
                          {standardUploadProgress.total > 0 ? ` • ${formatFileSize(standardUploadProgress.loaded)} / ${formatFileSize(standardUploadProgress.total)}` : ''}
                        </small>
                      </div>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn btn-light" onClick={requestCloseUploadModal}>{uploading ? 'Cancel Upload' : 'Cancel'}</button>
                    <button type="submit" className="btn btn-primary" disabled={uploading}>{uploading ? 'Uploading...' : 'Upload'}</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showFolderLimitModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header document-bucket-modal-header">
                  <h5 className="modal-title mb-0">Set Folder File Limit</h5>
                  <button type="button" className="btn-close" onClick={() => setShowFolderLimitModal(false)} />
                </div>
                <div className="modal-body">
                  <label className="form-label">Max files allowed in one folder upload</label>
                  <input
                    type="number"
                    min="1"
                    max="5000"
                    className="form-control"
                    value={folderLimitInput}
                    onChange={(event) => setFolderLimitInput(event.target.value)}
                  />
                  <small className="text-muted mt-2 d-block">Only admin/founder can change this limit.</small>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-light" onClick={() => setShowFolderLimitModal(false)} disabled={savingFolderLimit}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveFolderFileLimit} disabled={savingFolderLimit}>{savingFolderLimit ? 'Saving...' : 'Save Limit'}</button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {previewDoc.open && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-xl modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header document-bucket-modal-header">
                  <h5 className="modal-title mb-0 text-truncate">{previewDoc.title || 'Document Preview'}</h5>
                  <button type="button" className="btn-close" onClick={closePreview} />
                </div>
                <div className="document-bucket-preview-toolbar">
                  <div className="d-flex align-items-center gap-2 min-w-0">
                    {renderDocumentIcon(previewDoc.mode === 'folder' ? { row_type: 'folder' } : { original_file_name: previewDoc.title })}
                    <span className="text-truncate">{previewDoc.mode === 'folder' ? 'Folder Preview' : 'File Preview'}</span>
                  </div>
                  <div className="document-bucket-zoom-controls">
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => updatePreviewZoom((value) => value - 0.1)} disabled={previewZoom <= 0.2}>
                      <i className="ri-zoom-out-line me-1" /> Zoom Out
                    </button>
                    <span className="document-bucket-zoom-value">{Math.round(previewZoom * 100)}%</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => updatePreviewZoom((value) => value + 0.1)} disabled={previewZoom >= 2}>
                      <i className="ri-zoom-in-line me-1" /> Zoom In
                    </button>
                    <button type="button" className="btn btn-sm btn-light" onClick={() => updatePreviewZoom(1)}>Reset</button>
                  </div>
                </div>
                <div className="modal-body p-0 document-bucket-preview-body">
                  {previewDoc.mode === 'folder' ? (
                    <div className="row g-0">
                      <div className="col-12 col-lg-4 border-end">
                        <div className="list-group list-group-flush p-2">
                          {previewFolderTree ? renderPreviewTreeNode(previewFolderTree) : (
                            <div className="text-muted px-2 py-3">Folder structure not available</div>
                          )}
                        </div>
                      </div>
                      <div className="col-12 col-lg-8">
                        {renderPreviewFrame()}
                      </div>
                    </div>
                  ) : (
                    renderPreviewFrame()
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {showAccessModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-modal="true" role="dialog">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg">
                <div className="modal-header document-bucket-modal-header document-bucket-sticky-modal-header">
                  <div>
                    <h5 className="modal-title mb-0">Manage Access</h5>
                    <small className="text-muted">
                      {accessResource?.resource_type === 'folder' ? 'Folder' : 'Document'}: {accessResource?.title || 'Loading...'}
                    </small>
                  </div>
                  <button type="button" className="btn-close" onClick={closeAccessModal} disabled={savingAccessModal} />
                </div>
                <div className="modal-body">
                  {accessResource && (
                    <div className="alert alert-light border">
                      <div className="fw-semibold">Owner: {accessResource.owner_name || 'Unknown'}</div>
                      <small className="text-muted">{accessResource.owner_post || accessResource.owner_type || 'Owner'}</small>
                    </div>
                  )}

                  <div className="document-bucket-input-wrap mb-3">
                    <i className="ri-search-line" />
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by staff name, post, or ID"
                      value={accessSearch}
                      onChange={(event) => setAccessSearch(event.target.value)}
                      disabled={loadingAccessModal}
                    />
                  </div>

                  {loadingAccessModal ? (
                    <div className="py-4 text-center text-muted">Loading staff access list...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Staff</th>
                            <th style={{ width: '120px' }}>View</th>
                            <th style={{ width: '120px' }}>Edit</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAccessStaffRows.length > 0 ? filteredAccessStaffRows.map((entry) => (
                            <tr key={entry.staff_id}>
                              <td>
                                <div className="fw-semibold">{entry.name || `Staff ${entry.staff_id}`}</div>
                                <small className="text-muted">{entry.post || 'N/A'} | ID: {entry.staff_id}</small>
                                {entry.is_owner ? <span className="badge bg-primary-subtle text-primary border ms-2">Owner</span> : null}
                              </td>
                              <td>
                                <div className={`form-check form-switch m-0 document-bucket-modal-access-switch${entry.is_owner ? ' document-bucket-modal-access-switch-owner' : ''}`}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={Boolean(entry.can_view)}
                                    disabled={entry.is_owner || savingAccessModal}
                                    onChange={(event) => handleAccessToggle(entry.staff_id, 'can_view', event.target.checked)}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className={`form-check form-switch m-0 document-bucket-modal-access-switch${entry.is_owner ? ' document-bucket-modal-access-switch-owner' : ''}`}>
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={Boolean(entry.can_edit)}
                                    disabled={entry.is_owner || savingAccessModal}
                                    onChange={(event) => handleAccessToggle(entry.staff_id, 'can_edit', event.target.checked)}
                                  />
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="3" className="text-center text-muted py-4">No staff found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer document-bucket-sticky-modal-footer">
                  <button type="button" className="btn btn-light" onClick={closeAccessModal} disabled={savingAccessModal}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveAccessModal} disabled={loadingAccessModal || savingAccessModal}>
                    {savingAccessModal ? 'Saving...' : 'Save Access'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}

export default DocumentBucket;
